<script>
  import firebase from 'firebase';
  import {writable, derived} from 'svelte/store';
  import Loading from "../general/Loading.svelte";
  import {onDestroy, tick} from 'svelte';
  import MessageListItem from "./MessageListItem.svelte";
  import _ from 'lodash';
  import {getFirstDocument, subscribeMessages} from "./messageHelper";
  import moment from 'moment';

  export let channelId;
  export let onItemClick = null;

  // read only
  export let lastMessageId = null;

  const PAGE_SIZE = 20;
  const LOAD_MORE_THRESHOLD = 100;

  let unsubscribers = [];

  let containerElement = null;
  // let contentElement = null;
  let loading = false;
  let end = false;
  let initialized = false;

  const channelIdStore = writable(null);

  const messages = writable([]);
  let _rawMessages = {};

  const unsubscribeMessage = messages.subscribe(val => {
    lastMessageId = _.get(_.first(val), 'id', null);
  });


  const onSnapshot = snapshot => {
    // HACK: sometimes loading is not set to false when you change channels quickly
    loading = false;
    const updateTargetDocs = {};
    _.forEach(snapshot.docChanges(), change => {

      // if channelId has changed while loading data.
      // it's bit inefficient to do this here but doesn't happen often
      if (change.doc.data().channelId !== $channelIdStore) {
        return false;
      }

      if (change.type === 'removed') {
        _rawMessages = _.omit(_rawMessages, change.doc.id);
        return;
      }
      updateTargetDocs[change.doc.id] = change.doc;
    });

    // load more when it's the first loading and contents doesn't fill container
    _rawMessages = {
      ..._rawMessages,
      ...updateTargetDocs
    };

    messages.set(_.orderBy(_rawMessages, [m => m.data().created.seconds], 'desc'));
  };

  const loadNextMessages = async channelId => {
    loading = true;

    let startAt;
    if (_.isEmpty(_rawMessages)) {
      startAt = new firebase.firestore.Timestamp(Math.floor(Date.now() / 1000), 0);
      // subscribe new messages
      unsubscribers.push(subscribeMessages(channelId, onSnapshot, {
        limit: null,
        endBefore: startAt
      }));
    }

    unsubscribers.push(subscribeMessages(channelId, async snapshot => {
      onSnapshot(snapshot);

      loading = false;

      if (_.isEmpty(snapshot.docs)) {
        end = true;
      }

      // if not initial loading or no more docs left
      if (initialized) {
        return;
      }


      await tick();
      // containerElement.scrollTop = contentElement.offsetHeight;

      if (_.isEmpty(snapshot.docs)) return;

      // containerElement is null sometimes, especially when you change channels quickly
      if (containerElement.lastElementChild.getBoundingClientRect().y > LOAD_MORE_THRESHOLD) {
        loadNextMessages(channelId);
      } else {
        initialized = true;
      }
    }, {
      startAt,
      startAfter: startAt ? null : _.last($messages),
      limit: PAGE_SIZE
    }));
  };

  const unsubscribeChannelId = channelIdStore.subscribe(channelId => {
    initialized = false;
    end = false;
    loading = false;
    _.forEach(unsubscribers, u => u());
    _rawMessages = {};
    messages.set([]);

    if (!channelId) return;

    loadNextMessages(channelId);
  });


  $: channelIdStore.set(channelId);


  onDestroy(() => {
    _.forEach(unsubscribers, u => u());
    unsubscribeMessage();
    unsubscribeChannelId();
  });


  const showProfile = (messages, index) => {
    if (index >= _.size(messages)) return false;

    if (index === _.size(messages) - 1) {
      // show profile for last message - last message will be shown on top
      return true;
    }

    const message = messages[index];
    const prevMessage = messages[index + 1]; // message with higher index will be shown above

    const currentCreated = message.data().created.seconds;
    const prevCreated = prevMessage.data().created.seconds;

    if (currentCreated - prevCreated > 60) return true;

    const currentCreatedDate = new Date(message.data().created.seconds * 1000);
    const prevCreatedDate = new Date(prevMessage.data().created.seconds * 1000);

    // message after date indicator
    if (currentCreatedDate.toDateString() !== prevCreatedDate.toDateString()) return true;

    if (message.data().userId !== prevMessage.data().userId) return true;

    return false;
  };

  const onScroll = e => {
    if (end) return;
    if (loading) return;
    if (e.target.scrollTop < LOAD_MORE_THRESHOLD) {
      loadNextMessages(channelId);
    }
  };

  const showDate = (messages, index) => {
    if (index === _.size(messages) - 1) return true;

    const current = messages[index];
    const next = messages[index + 1];

    const currentDate = current.data().created.toDate().toLocaleDateString();
    const nextDate = next.data().created.toDate().toLocaleDateString();

    return currentDate !== nextDate;
  }

</script>
<column class="container" reverse bind:this={containerElement} on:scroll={onScroll}>
    {#each $messages as message, index}
      <MessageListItem message={message} onMessageClick={onItemClick} showProfile={showProfile($messages, index)}/>

        {#if showDate($messages, index)}
          <row align-center>
            <div class="horizontal-line expand"></div>
              <div class="date">{moment(message.data().created.toDate()).format('ll')}</div>
            <div class="horizontal-line expand"></div>
          </row>
        {/if}
    {/each}
    {#if loading}
      <Loading style="margin: 8px;"/>
    {/if}
</column>


<style>
  .container {
    width: 100%;
    height: 100%;
    overflow-y: auto;
    position: relative;
  }

  .horizontal-line {
    height: 1px;
    background-color: #ddd;
  }

  .date {
    margin: 12px;
    font-size: .8rem;
    font-weight: bold;
    color: #555;
  }

</style>